import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  BookOpen, 
  Image, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  ToggleRight,
  Upload,
  Copy,
  Users,
  ExternalLink,
  X,
  Download,
  FileText,
  Sparkles,
  FolderTree,
  Layers,
  MoreVertical,
  ChevronDown
} from 'lucide-react';
import DataTable, { Column, Action } from '../components/DataTable';
import ProductForm from '../components/ProductForm';
import ToolModal from '../components/ToolModal';
import { Product, Tutorial, apiService } from '../services/api';
import { useProducts, useTutorials, useAllTutorials } from '../hooks/useApi';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from './UserManagement';
import SettingsPage from './SettingsPage';
import TemplateManager from '../components/TemplateManager';
import HierarchyManager from '../components/HierarchyManager';
import CollectionManager from '../components/CollectionManager';
import glossaryService from '../services/glossaryService';

interface MediaItem {
  id: string;
  name: string;
  url: string;
  size: string;
  type: string;
  uploadedAt: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  lastActive: string;
  status: 'active' | 'inactive';
}

const AdminPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isManage = location.pathname.startsWith('/manage') || location.pathname.startsWith('/admin');
  const [activeTab, setActiveTab] = useState('products');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [selectedProductFilter, setSelectedProductFilter] = useState<string>('all');
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState<'products' | 'tutorials'>('products');
  const [importData, setImportData] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedTutorials, setSelectedTutorials] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showTutorialActionsMenu, setShowTutorialActionsMenu] = useState(false);
  const { user } = useAuth();
  const canCreate = user?.roles?.includes('admin') || user?.roles?.includes('instructor');

  // API hooks
  const { data: productsData, loading: productsLoading, error: productsError, refetch: refetchProducts } = useProducts();
  // Fetch all tutorials using paginated requests (backend limit is 100)
  const { data: tutorialsData, loading: tutorialsLoading, error: tutorialsError, refetch: refetchTutorials } = useAllTutorials('all');
  
  const products = productsData?.products || [];
  const tutorials = tutorialsData?.tutorials || [];
  const totalTutorials = tutorialsData?.pagination?.total || tutorials.length;

  // Ensure glossary is loaded for content management
  useEffect(() => {
    glossaryService.refreshFromAPI().catch((error) => {
      console.error('Failed to refresh glossary in AdminPage:', error);
    });
  }, []);
  
  // Handle URL tab from path or query parameter (for backward compatibility)
  useEffect(() => {
    const allowedTabs = isManage
      ? ['products', 'tutorials', 'hierarchy', 'templates', 'glossary', 'collections']
      : ['users', 'settings', 'media'];
    
    // Extract tab from URL path
    const path = location.pathname;
    let tabFromPath: string | null = null;
    if (path.startsWith('/admin/')) {
      const tab = path.split('/admin/')[1];
      if (tab && tab.length > 0) {
        tabFromPath = tab;
      }
    } else if (path.startsWith('/manage/')) {
      const tab = path.split('/manage/')[1];
      if (tab && tab.length > 0) {
        tabFromPath = tab;
      }
    }
    
    // First try to get tab from URL path
    if (tabFromPath && allowedTabs.includes(tabFromPath)) {
      setActiveTab(tabFromPath);
      return;
    }
    
    // Fallback to query parameter for backward compatibility
    const tabParam = searchParams.get('tab');
    if (tabParam && allowedTabs.includes(tabParam)) {
      setActiveTab(tabParam);
      // Update URL to use path instead of query param
      navigate(`/admin/${tabParam}`, { replace: true });
    } else {
      const defaultTab = isManage ? 'products' : 'users';
      setActiveTab(defaultTab);
      // Update URL to use path
      if (location.pathname === '/admin' || location.pathname === '/manage') {
        navigate(`/admin/${defaultTab}`, { replace: true });
      }
    }
  }, [location.pathname, searchParams, isManage, navigate]);

  // Filter tutorials by product
  const filteredTutorials = selectedProductFilter === 'all' 
    ? tutorials 
    : tutorials.filter(tutorial => tutorial.product === selectedProductFilter);

  // Handlers
  const handleEditTutorial = (tutorial: Tutorial) => {
    // Navigate to manual tutorial builder with edit mode
    navigate(`/builder/${tutorial.id}`);
  };

  const handleEditTutorialWithAI = (tutorial: Tutorial) => {
    // Navigate to AI-assisted tutorial builder with edit mode
    navigate(`/builder/ai-assisted/${tutorial.id}`);
  };

  const handleDeleteTutorial = async (tutorial: Tutorial) => {
    if (window.confirm(`Are you sure you want to delete "${tutorial.title}"?`)) {
      try {
        // TODO: Implement delete tutorial API call
        toast.success('Tutorial deleted successfully');
        refetchTutorials();
      } catch (error) {
        toast.error('Failed to delete tutorial');
      }
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleViewProduct = (product: Product) => {
    setViewingProduct(product);
    setShowProductModal(true);
  };

  const handleOpenTool = (product: Product) => {
    if (product.url) {
      window.open(product.url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('No tool URL available for this product');
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      try {
        await apiService.deleteProduct(product.id);
        toast.success('Product deleted successfully');
        refetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error('Please enter JSON data to import');
      return;
    }

    setIsImporting(true);
    try {
      const data = JSON.parse(importData);
      
      if (importType === 'products') {
        if (Array.isArray(data)) {
          // Import multiple products
          for (const product of data) {
            await apiService.createProduct(product);
          }
          toast.success(`Successfully imported ${data.length} products`);
        } else {
          // Import single product
          await apiService.createProduct(data);
          toast.success('Successfully imported 1 product');
        }
        refetchProducts();
      } else {
        if (Array.isArray(data)) {
          // Import multiple tutorials
          for (const tutorial of data) {
            await apiService.createTutorial(tutorial);
          }
          toast.success(`Successfully imported ${data.length} tutorials`);
        } else {
          // Import single tutorial
          await apiService.createTutorial(data);
          toast.success('Successfully imported 1 tutorial');
        }
        refetchTutorials();
      }
      
      setShowImportModal(false);
      setImportData('');
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import data. Please check your JSON format.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setImportData(content);
      };
      reader.readAsText(file);
    }
  };

  const handleExport = async (type: 'products' | 'tutorials', selectedOnly: boolean = false) => {
    setIsExporting(true);
    try {
      let data: any;
      let filename: string;
      
      if (type === 'products') {
        if (selectedOnly && selectedProducts.length > 0) {
          data = products.filter(product => selectedProducts.includes(product.id));
          filename = `products-selected-${selectedProducts.length}-export.json`;
        } else {
          data = products;
          filename = 'products-export.json';
        }
      } else {
        if (selectedOnly && selectedTutorials.length > 0) {
          data = tutorials.filter(tutorial => selectedTutorials.includes(tutorial.id));
          filename = `tutorials-selected-${selectedTutorials.length}-export.json`;
        } else {
          data = tutorials;
          filename = 'tutorials-export.json';
        }
      }

      // Clean the data for export (remove internal fields)
      const cleanData = data.map((item: any) => {
        const { id, createdAt, updatedAt, authorId, resources, ...cleanItem } = item;
        
        // Fix isPublished to be boolean (convert 0/1 to false/true)
        if (cleanItem.isPublished !== undefined) {
          cleanItem.isPublished = Boolean(cleanItem.isPublished);
        }
        
        // For tutorials, also remove internal fields from steps
        if (type === 'tutorials' && cleanItem.steps) {
          cleanItem.steps = cleanItem.steps.map((step: any) => {
            const { 
              id: stepId, 
              tutorialId, 
              order_index, 
              language, 
              description, 
              createdAt: stepCreatedAt, 
              updatedAt: stepUpdatedAt, 
              ...cleanStep 
            } = step;
            
            // Fix quiz questions - ensure they have required fields and remove id
            if (cleanStep.type === 'quiz' && cleanStep.questions) {
              cleanStep.questions = cleanStep.questions.map((question: any) => {
                const { id: questionId, ...cleanQuestion } = question;
                return {
                  ...cleanQuestion,
                  title: question.title || question.question?.substring(0, 50) + '...' || 'Quiz Question'
                };
              });
            }
            
            return cleanStep;
          });
        }
        
        return cleanItem;
      });

      // Create and download the file
      const jsonString = JSON.stringify(cleanData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Successfully exported ${cleanData.length} ${type}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select products to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedProducts.length} products? This action cannot be undone.`)) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      const response = await apiService.bulkDeleteProducts(selectedProducts);
      if (response.success) {
        toast.success(response.message);
        setSelectedProducts([]);
        refetchProducts();
      } else {
        toast.error('Failed to delete products');
      }
    } catch (error) {
      console.error('Bulk delete products error:', error);
      toast.error('Failed to delete products. Please try again.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDeleteTutorials = async () => {
    if (selectedTutorials.length === 0) {
      toast.error('Please select tutorials to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedTutorials.length} tutorials? This action cannot be undone.`)) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      const response = await apiService.bulkDeleteTutorials(selectedTutorials);
      if (response.success) {
        toast.success(response.message);
        setSelectedTutorials([]);
        refetchTutorials();
      } else {
        toast.error('Failed to delete tutorials');
      }
    } catch (error) {
      console.error('Bulk delete tutorials error:', error);
      toast.error('Failed to delete tutorials. Please try again.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleCreateProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setIsCreatingProduct(true);
      await apiService.createProduct(productData);
      toast.success('Product created successfully!');
      refetchProducts(); // Refresh the products list
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Failed to create product. Please try again.');
      throw error; // Re-throw to let the form handle the error state
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const handleUpdateProduct = async (productData: Partial<Product>) => {
    if (!editingProduct) return;
    
    try {
      await apiService.updateProduct(editingProduct.id, productData);
      toast.success('Product updated successfully!');
      refetchProducts(); // Refresh the products list
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product. Please try again.');
      throw error; // Re-throw to let the form handle the error state
    }
  };

  // Mock media data
  const [mediaItems] = useState<MediaItem[]>([
    {
      id: '1',
      name: 'bomg-screenshot.png',
      url: '/images/bomg-screenshot.png',
      size: '2.4 MB',
      type: 'image/png',
      uploadedAt: '2024-01-15'
    },
    {
      id: '2',
      name: 'product-master-logo.svg',
      url: '/images/product-master-logo.svg',
      size: '156 KB',
      type: 'image/svg+xml',
      uploadedAt: '2024-01-14'
    },
    {
      id: '3',
      name: 'pricing-engine-demo.mp4',
      url: '/videos/pricing-engine-demo.mp4',
      size: '45.2 MB',
      type: 'video/mp4',
      uploadedAt: '2024-01-13'
    }
  ]);

  // Mock users data
  const [users] = useState<User[]>([
    {
      id: '1',
      name: 'John Smith',
      email: 'john@viaphoton.com',
      role: 'Admin',
      lastActive: '2024-01-15',
      status: 'active'
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      email: 'sarah@viaphoton.com',
      role: 'Editor',
      lastActive: '2024-01-14',
      status: 'active'
    },
    {
      id: '3',
      name: 'Mike Wilson',
      email: 'mike@viaphoton.com',
      role: 'Viewer',
      lastActive: '2024-01-12',
      status: 'inactive'
    }
  ]);

  const tabs = isManage
    ? [
        { id: 'products', label: 'Products', icon: Package },
        { id: 'tutorials', label: 'Tutorials', icon: BookOpen },
        { id: 'hierarchy', label: 'Hierarchy', icon: FolderTree },
        { id: 'collections', label: 'Collections', icon: Layers },
        { id: 'templates', label: 'Templates', icon: FileText },
        { id: 'glossary', label: 'Glossary', icon: Settings }
      ]
    : [
        { id: 'users', label: 'Users', icon: Users },
        { id: 'media', label: 'Media Library', icon: Image },
        { id: 'settings', label: 'Settings', icon: Settings }
      ];

  // Product columns
  const productColumns: Column<Product>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, item) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-lg flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{item.tagline}</div>
          </div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={cn(
          "px-2 py-1 text-xs font-medium rounded-full",
          value === 'operational' 
            ? "bg-green-100 text-green-800" 
            : "bg-yellow-100 text-yellow-800"
        )}>
          {value}
        </span>
      )
    },
    {
      key: 'users',
      label: 'Users',
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value.length} users
        </div>
      )
    }
  ];

  // Product actions
  const productActions: Action<Product>[] = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: handleEditProduct
    },
    {
      label: 'View',
      icon: Eye,
      onClick: handleViewProduct
    },
    {
      label: 'Link to Tool',
      icon: ExternalLink,
      onClick: handleOpenTool
    },
    {
      label: 'Toggle Status',
      icon: ToggleRight,
      onClick: (product) => {}
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDeleteProduct
    }
  ];

  // Tutorial columns
  const tutorialColumns: Column<Tutorial>[] = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (value, item) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate" title={value}>{value}</div>
          <div className="text-sm text-gray-500 truncate" title={item.description}>
            {item.description && item.description.length > 60 
              ? `${item.description.substring(0, 60)}...` 
              : item.description || 'No description'
            }
          </div>
        </div>
      )
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      sortable: true,
      width: '80px',
      render: (value) => (
        <div className="flex items-center justify-center">
          <span className={cn(
            "w-2 h-2 rounded-full",
            value === 'beginner' ? "bg-green-500" :
            value === 'intermediate' ? "bg-yellow-500" :
            "bg-red-500"
          )} title={value} />
        </div>
      )
    },
    {
      key: 'estimatedTime',
      label: 'Duration',
      sortable: true,
      width: '100px',
      render: (value) => (
        <span className="text-sm text-gray-600">
          {typeof value === 'string' ? value.replace(' minutes', 'm').replace(' minute', 'm').replace(' min', 'm') : value}
        </span>
      )
    },
    {
      key: 'steps',
      label: 'Steps',
      width: '70px',
      render: (value) => (
        <div className="text-sm text-gray-600 text-center">
          {value?.length || 0}
        </div>
      )
    },
  ];

  // Tutorial actions
  const tutorialActions: Action<Tutorial>[] = [
    {
      label: 'Edit (Manual)',
      icon: Edit,
      onClick: handleEditTutorial
    },
    {
      label: 'Edit with AI',
      icon: Sparkles,
      onClick: handleEditTutorialWithAI
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDeleteTutorial
    },
    {
      label: 'Publish',
      icon: ToggleRight,
      onClick: (tutorial) => {}
    }
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const renderProductsTab = () => {
    if (productsLoading) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Products Management</h2>
            <div className="w-32 h-10 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      );
    }

    if (productsError) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Products Management</h2>
          </div>
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading products: {productsError}</p>
            <button
              onClick={refetchProducts}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Products Management</h2>
          <div className="flex items-center space-x-3" data-tour="admin-actions">
            <button
              onClick={() => handleExport('products', false)}
              disabled={isExporting || products.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Download className="w-4 h-4" />
              <span>{isExporting ? 'Exporting...' : 'Export All Products'}</span>
            </button>
            <button
              onClick={() => handleExport('products', true)}
              disabled={isExporting || selectedProducts.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Download className="w-4 h-4" />
              <span>{isExporting ? 'Exporting...' : `Export Selected (${selectedProducts.length})`}</span>
            </button>
            <button
              onClick={() => {
                setImportType('products');
                setShowImportModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
            >
              <Upload className="w-4 h-4" />
              <span>Import Products</span>
            </button>
            <button
              onClick={handleBulkDeleteProducts}
              disabled={isBulkDeleting || selectedProducts.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Trash2 className="w-4 h-4" />
              <span>
                {isBulkDeleting ? 'Deleting...' : `Delete Selected (${selectedProducts.length})`}
              </span>
            </button>
            {canCreate && (
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setShowProductForm(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Add Product</span>
              </button>
            )}
          </div>
        </div>
        
        <DataTable
          data={products}
          columns={productColumns}
          actions={productActions}
          searchable
          pagination
          pageSize={10}
          selectable
          selectedItems={selectedProducts}
          onSelectionChange={setSelectedProducts}
          idField="id"
        />
      </div>
    );
  };

  const renderTutorialsTab = () => {
    if (tutorialsLoading) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Tutorials Management</h2>
            <div className="w-32 h-10 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      );
    }

    if (tutorialsError) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Tutorials Management</h2>
          </div>
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading tutorials: {tutorialsError}</p>
            <button
              onClick={refetchTutorials}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Header with compact actions */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Tutorials Management</h2>
            <p className="text-sm text-gray-500 mt-1">
              Showing {filteredTutorials.length} of {totalTutorials} tutorial{totalTutorials !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {canCreate && (
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openCreateContentModal'));
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Create</span>
              </button>
            )}
            {/* Actions Dropdown Menu */}
            <div className="relative">
              <button
                onClick={() => setShowTutorialActionsMenu(!showTutorialActionsMenu)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
              >
                <MoreVertical className="w-4 h-4" />
                <span>Actions</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showTutorialActionsMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowTutorialActionsMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                    <button
                      onClick={() => {
                        handleExport('tutorials', false);
                        setShowTutorialActionsMenu(false);
                      }}
                      disabled={isExporting || tutorials.length === 0}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export All Tutorials</span>
                    </button>
                    <button
                      onClick={() => {
                        handleExport('tutorials', true);
                        setShowTutorialActionsMenu(false);
                      }}
                      disabled={isExporting || selectedTutorials.length === 0}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export Selected ({selectedTutorials.length})</span>
                    </button>
                    <button
                      onClick={() => {
                        setImportType('tutorials');
                        setShowImportModal(true);
                        setShowTutorialActionsMenu(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Import Tutorials</span>
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={() => {
                        handleBulkDeleteTutorials();
                        setShowTutorialActionsMenu(false);
                      }}
                      disabled={isBulkDeleting || selectedTutorials.length === 0}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>
                        {isBulkDeleting ? 'Deleting...' : `Delete Selected (${selectedTutorials.length})`}
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar (shown when items are selected) */}
        {selectedTutorials.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm font-medium text-blue-900">
              {selectedTutorials.length} tutorial{selectedTutorials.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleExport('tutorials', true)}
                disabled={isExporting}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                <Download className="w-3 h-3" />
                <span>Export</span>
              </button>
              <button
                onClick={handleBulkDeleteTutorials}
                disabled={isBulkDeleting}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" />
                <span>Delete</span>
              </button>
              <button
                onClick={() => setSelectedTutorials([])}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <div className="flex items-center space-x-2">
            <Package className="w-4 h-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Product:</label>
            <select
              value={selectedProductFilter}
              onChange={(e) => setSelectedProductFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
            >
              <option value="all">All Products</option>
              {products.map((product) => (
                <option key={product.id} value={product.name}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          {selectedProductFilter !== 'all' && (
            <button
              onClick={() => setSelectedProductFilter('all')}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear Filter
            </button>
          )}
        </div>
        
        <DataTable
          data={filteredTutorials}
          columns={tutorialColumns}
          actions={tutorialActions}
          searchable
          pagination
          pageSize={50}
          selectable
          selectedItems={selectedTutorials}
          onSelectionChange={setSelectedTutorials}
          idField="id"
        />
      </div>
    );
  };

  const renderMediaTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Media Library</h2>
        <label className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all cursor-pointer">
          <Upload className="w-4 h-4" />
          <span>Upload Media</span>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {mediaItems.map((item) => (
          <motion.div
            key={item.id}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="aspect-video bg-gray-100 flex items-center justify-center">
              {item.type.startsWith('image/') ? (
                <Image className="w-12 h-12 text-gray-400" />
              ) : (
                <Upload className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <div className="p-4">
              <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
              <p className="text-sm text-gray-500">{item.size}</p>
              <p className="text-xs text-gray-400">{item.uploadedAt}</p>
              
              <div className="flex items-center space-x-2 mt-3">
                <button
                  onClick={() => copyToClipboard(item.url)}
                  className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  <span>Copy URL</span>
                </button>
                <button
                  onClick={() => {}}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      <SettingsPage />
    </div>
  );

  const renderUsersTab = () => (
    <div className="space-y-6">
      <UserManagement />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4" data-tour="admin-header">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Welcome, Admin</span>
            <div className="w-8 h-8 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">A</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <nav className="p-6" data-tour="admin-tabs">
            <div className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(`/admin/${tab.id}`);
                  }}
                  className={cn(
                    "w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors",
                    activeTab === tab.id
                      ? "bg-blue-50 text-[#667eea] border border-blue-200"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'products' && isManage && renderProductsTab()}
              {activeTab === 'tutorials' && isManage && renderTutorialsTab()}
              {activeTab === 'hierarchy' && isManage && <HierarchyManager />}
              {activeTab === 'collections' && isManage && <CollectionManager />}
              {activeTab === 'templates' && isManage && <TemplateManager />}
              {activeTab === 'glossary' && isManage && renderSettingsTab()}
              {activeTab === 'media' && !isManage && renderMediaTab()}
              {activeTab === 'settings' && !isManage && renderSettingsTab()}
              {activeTab === 'users' && !isManage && renderUsersTab()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductForm
          product={editingProduct || undefined}
          onSave={editingProduct ? handleUpdateProduct : handleCreateProduct}
          onClose={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          isLoading={isCreatingProduct}
        />
      )}

      {/* Product View Modal */}
      {showProductModal && viewingProduct && (
        <ToolModal
          product={viewingProduct}
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setViewingProduct(null);
          }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Import {importType === 'products' ? 'Products' : 'Tutorials'}
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Import Type
                </label>
                <select
                  value={importType}
                  onChange={(e) => setImportType(e.target.value as 'products' | 'tutorials')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="products">Products</option>
                  <option value="tutorials">Tutorials</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload JSON File
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Or paste JSON data
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  placeholder={`Paste your ${importType} JSON data here...`}
                  className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting || !importData.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isImporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Importing...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Import</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
